import { Analytics } from './analytics/analytics';
import { catalogItem } from './config/catalog';
import {
  createOnboardingFlags,
  resolveOnboardingStep,
  type OnboardingFlags,
} from './onboarding/onboarding';
import { applyIncomeTicks, applyOfflineEarnings, collectAllStoredIncome, computeParkScore } from './sim/economy';
import {
  collectEntity,
  expandLand,
  expandLandWithChargedCost,
  moveEntity,
  paintFloorCell,
  placeEntity,
  placeEntityWithChargedCost,
  removeEntity,
  rotateEntity,
  setEntityPalette,
  upgradeEntity,
  createInitialState,
} from './sim/park';
import { expansionCost, FIRST_SESSION_REWARD_COINS, SAVE_VERSION } from './config/balancing';
import {
  attractionEffectiveCost,
  consumeAttractionBuildCoupon,
  consumeLandDiscount,
  hasAttractionBuildCoupon,
  isAutoCollectActive,
  isEconomyPaused,
  landEffectiveCost,
} from './platform/rewardedAds';
import { peekSaveVersion, parseSave, retainMigrationBackup, serializePark } from './sim/save';
import { hasBuiltKind } from './sim/unlocks';
import type { PersistenceProvider } from './persistence/localStorage';
import type { ActionResult, EntityKind, ParkState, RotationQuarter, SubcellAnchor } from './types';
import type { FloorPaintStyle } from './sim/floors';

/** Derived auto-collect from Drink and Spinning built kinds. Does not touch the rewarded timer. */
export function derivedAutoCollectActive(state: ParkState): boolean {
  return hasBuiltKind(state, 'drink_kiosk') && hasBuiltKind(state, 'spinning_ride');
}

export class GameSession {
  readonly state: ParkState;
  readonly offlineGranted: number;
  readonly startedAt: number;
  readonly onboarding: OnboardingFlags = createOnboardingFlags();
  buildMode = false;
  selectedSlotId: string | null = null;
  offlineOfferClosed = false;
  private trackedFirstBuild: boolean;
  private pendingMigrationBackup: string | null = null;
  /** Coins just granted for the guided Drink. Cleared when the HUD reads it. Not saved. */
  private firstRewardNotice = 0;
  /** Session-only. `auto_collect_started` fires once when derived auto-collect is first observed. */
  private derivedAutoCollectTracked = false;

  constructor(
    private readonly persistence: PersistenceProvider,
    now = Date.now(),
  ) {
    const raw = persistence.load();
    const loadedVersion = raw ? peekSaveVersion(raw) : null;
    if (raw && loadedVersion != null && loadedVersion < SAVE_VERSION) {
      this.pendingMigrationBackup = raw;
    }
    this.state = parseSave(raw, now);
    this.startedAt = now;
    this.trackedFirstBuild = this.state.entities.length > 0;
    const offline = applyOfflineEarnings(this.state, now);
    this.offlineGranted = offline.granted;
    this.persist(now);
  }

  persist(now = Date.now()): void {
    if (this.pendingMigrationBackup) {
      retainMigrationBackup(this.pendingMigrationBackup);
      this.pendingMigrationBackup = null;
    }
    this.state.lastSaveTimestamp = now;
    this.persistence.save(serializePark(this.state));
  }

  clearSave(): void {
    this.persistence.clear();
  }

  /** DEV reset: write a fresh park so beforeunload persist cannot restore the old save. */
  resetPark(now = Date.now()): void {
    const fresh = createInitialState(now);
    this.state.coins = fresh.coins;
    this.state.unlockedLandIds.splice(0, this.state.unlockedLandIds.length, ...fresh.unlockedLandIds);
    this.state.entities.length = 0;
    this.state.builtKindMilestones = [];
    this.state.floors = undefined;
    this.trackedFirstBuild = false;
    this.firstRewardNotice = 0;
    this.derivedAutoCollectTracked = false;
    this.buildMode = false;
    this.selectedSlotId = null;
    Object.assign(this.onboarding, createOnboardingFlags());
    this.persist(now);
  }

  parkScore(): number {
    return computeParkScore(this.state);
  }

  tickIncome(ticks = 1, now = Date.now()): void {
    if (isEconomyPaused()) {
      return;
    }
    applyIncomeTicks(this.state, ticks);
    const derived = derivedAutoCollectActive(this.state);
    if (derived) {
      this.noteDerivedAutoCollectStarted();
    }
    if (isAutoCollectActive(now) || derived) {
      collectAllStoredIncome(this.state);
    }
  }

  private noteDerivedAutoCollectStarted(): void {
    if (this.derivedAutoCollectTracked) {
      return;
    }
    this.derivedAutoCollectTracked = true;
    Analytics.track('auto_collect_started');
  }

  /** Transfer every entity's stored income into coins and persist that result once. */
  collectAll(now = Date.now()): number {
    const amount = collectAllStoredIncome(this.state);
    if (amount <= 0) {
      return 0;
    }
    Analytics.track('collect_all', { amount });
    this.persist(now);
    return amount;
  }

  closeOfflineOffer(): void {
    this.offlineOfferClosed = true;
  }

  /** Amount of the first-session reward waiting for the completion toast. Zero if none. */
  consumeFirstRewardNotice(): number {
    const amount = this.firstRewardNotice;
    this.firstRewardNotice = 0;
    return amount;
  }

  grantOfflineDoubleBonus(): number {
    if (this.offlineGranted <= 0 || this.offlineOfferClosed) {
      return 0;
    }
    this.state.coins += this.offlineGranted;
    this.offlineOfferClosed = true;
    this.persist();
    return this.offlineGranted;
  }

  place(
    slotId: string,
    kind: EntityKind,
    options?: { anchor?: SubcellAnchor; rotation?: RotationQuarter },
  ): ActionResult {
    const wasEmpty = this.state.entities.length === 0;
    // Guided Drink is the only grant. `build_drink` means Starter and Snack are
    // already built and Drink is not yet a milestone. A successful place records
    // that milestone in the same persist, so a reload is `complete` and cannot pay again.
    const grantFirstReward =
      kind === 'drink_kiosk' && resolveOnboardingStep(this.state, this.onboarding) === 'build_drink';
    const role = catalogItem(kind).role;
    const coupon = role === 'attraction' && hasAttractionBuildCoupon();
    const result = coupon
      ? placeEntityWithChargedCost(this.state, slotId, kind, attractionEffectiveCost(kind, true), options)
      : placeEntity(this.state, slotId, kind, options);
    if (!result.ok) {
      return result;
    }
    if (grantFirstReward) {
      this.state.coins += FIRST_SESSION_REWARD_COINS;
      this.firstRewardNotice = FIRST_SESSION_REWARD_COINS;
    }
    if (coupon) {
      consumeAttractionBuildCoupon();
    }
    if (wasEmpty && !this.trackedFirstBuild) {
      this.trackedFirstBuild = true;
      Analytics.track('first_build', { kind, slotId });
    }
    if (role === 'attraction') {
      Analytics.track('attraction_built', { kind, slotId });
    } else if (role === 'kiosk') {
      Analytics.track('kiosk_built', { kind, slotId });
    } else {
      Analytics.track('decoration_built', { kind, slotId });
    }
    this.persist();
    return result;
  }

  upgrade(entityId: string): ActionResult {
    const result = upgradeEntity(this.state, entityId);
    if (!result.ok) {
      return result;
    }
    Analytics.track('entity_upgrade', { entityId });
    this.persist();
    return result;
  }

  collect(entityId: string): ActionResult & { amount?: number } {
    const step = resolveOnboardingStep(this.state, this.onboarding);
    const result = collectEntity(this.state, entityId);
    if (!result.ok) {
      return result;
    }
    if (step === 'collect_drink' && (result.amount ?? 0) > 0 && !this.onboarding.tutorialCollectTracked) {
      this.onboarding.tutorialCollectTracked = true;
      Analytics.track('tutorial_collect', { entityId });
    }
    this.persist();
    return result;
  }

  expand(parcelId: string): ActionResult {
    const voucherCost = landEffectiveCost(expansionCost(parcelId), parcelId);
    const discounted = voucherCost !== expansionCost(parcelId);
    const result = discounted
      ? expandLandWithChargedCost(this.state, parcelId, voucherCost)
      : expandLand(this.state, parcelId);
    if (!result.ok) {
      return result;
    }
    if (discounted) {
      consumeLandDiscount(parcelId);
    }
    Analytics.track('land_expanded', { parcelId });
    this.persist();
    return result;
  }

  remove(entityId: string): ActionResult & { refund?: number } {
    const result = removeEntity(this.state, entityId);
    if (!result.ok) {
      return result;
    }
    this.persist();
    return result;
  }

  rotate(entityId: string): ActionResult {
    const result = rotateEntity(this.state, entityId);
    if (!result.ok) {
      return result;
    }
    this.persist();
    return result;
  }

  paintFloor(coord: SubcellAnchor, style: FloorPaintStyle): ActionResult {
    const result = paintFloorCell(this.state, coord, style);
    if (!result.ok) {
      return result;
    }
    this.persist();
    return result;
  }

  move(entityId: string, anchor: SubcellAnchor, rotation?: RotationQuarter): ActionResult {
    const result = moveEntity(this.state, entityId, anchor, rotation);
    if (!result.ok) {
      return result;
    }
    this.persist();
    return result;
  }

  setPalette(entityId: string, paletteId: string): ActionResult {
    const result = setEntityPalette(this.state, entityId, paletteId);
    if (!result.ok) {
      return result;
    }
    this.persist();
    return result;
  }
}
